/**
 * Presentational themed primitives — thin wrappers that render a widget's
 * stored content as a REAL design-system component (Duro by default; see
 * designSystemContext). No grid deps and no effects, so the same components
 * serve the editor canvas (display) and the publish render
 * (renderToStaticMarkup). Brand theming is applied once at the page scope, so
 * these only pick variants.
 *
 * This module is also bundled, by plain esbuild, into the design-system
 * sandbox's runtime (vite-ds-sandbox.ts): it imports nothing that needs the
 * StyleX pass (react-strict-dom, Duro) and nothing heavy (the schemas
 * barrel). test/dsSandboxRuntime.test.ts holds that line.
 */
import { createElement } from "react";

import { useDesignSystem } from "./designSystemContext.js";

type Level = 1 | 2 | 3 | 4 | 5 | 6;

/** Horizontal placement, in the design system's own vocabulary. */
export type Align = "start" | "center" | "end";
const ALIGNS = ["start", "center", "end"] as const;

/** A stored `align`, or undefined for anything else (the default: start). */
export function alignOf(v: unknown): Align | undefined {
  return typeof v === "string" && (ALIGNS as readonly string[]).includes(v)
    ? (v as Align)
    : undefined;
}

const BUTTON_VARIANTS = [
  "primary",
  "secondary",
  "inverseSecondary",
  "link",
  "danger",
] as const;
const CARD_VARIANTS = [
  "elevated",
  "outlined",
  "filled",
  "interactive",
] as const;

function oneOf<T extends string>(
  allowed: readonly T[],
  v: unknown,
  fallback: T,
): T {
  return typeof v === "string" && (allowed as readonly string[]).includes(v)
    ? (v as T)
    : fallback;
}

export interface ButtonContent {
  readonly label?: string;
  readonly href?: string;
  readonly variant?: string;
  readonly size?: string;
  /** Placement in its cell; omitted = start, rendered exactly as before. */
  readonly align?: string;
  /** "submit" submits the form its section is; `href` is ignored then. */
  readonly type?: string;
  /** "fill" takes the cell's width; "hug" (or absent), its own. */
  readonly width?: "hug" | "fill";
}
export function PrimitiveButton({
  label = "Button",
  href,
  variant,
  size,
  align,
  type,
  width,
}: ButtonContent) {
  const { Button, LinkButton, Inline } = useDesignSystem().components;
  const s = size === "small" ? "small" : "default";
  // Only passed when set, so a button without it renders exactly as before.
  const fill = width === "fill" ? { fullWidth: true } : {};
  const button =
    type === "submit" ? (
      <Button
        type="submit"
        variant={oneOf(BUTTON_VARIANTS, variant, "primary")}
        size={s}
        {...fill}
      >
        {label}
      </Button>
    ) : href ? (
      <LinkButton
        href={href}
        variant={variant === "secondary" ? "secondary" : "primary"}
        size={s}
        {...fill}
      >
        {label}
      </LinkButton>
    ) : (
      <Button
        variant={oneOf(BUTTON_VARIANTS, variant, "primary")}
        size={s}
        {...fill}
      >
        {label}
      </Button>
    );
  // Placement is the design system's Inline, not local layout CSS.
  const justify = width === "fill" ? undefined : alignOf(align);
  return justify ? <Inline justify={justify}>{button}</Inline> : button;
}

const INPUT_TYPES = ["text", "search", "email", "url", "tel"] as const;
/** The contract's glyph set: the schema's `InputIcon`, restated here so the
 *  sandbox bundle stays free of the schemas barrel; pinned equal by
 *  test/dsSandboxRuntime.test.ts. */
export const INPUT_ICONS = ["search", "mail", "lock", "key", "pin"] as const;

export interface InputContent {
  readonly name?: string;
  /** Accessible name (the schema requires one). */
  readonly label?: string;
  readonly placeholder?: string;
  readonly type?: string;
  readonly required?: boolean;
  readonly icon?: string;
}
/**
 * One text field: Duro `Input`, or `InputGroup` with a leading `Icon` addon.
 * Uncontrolled — its value belongs to the visitor (or, on the canvas, to
 * nobody: typing there never reaches the document).
 */
export function PrimitiveInput({
  name,
  label,
  placeholder,
  type,
  required,
  icon,
}: InputContent) {
  const { Input, InputGroup, Icon } = useDesignSystem().components;
  const t = oneOf(INPUT_TYPES, type, "text");
  const input = (
    <Input
      type={t}
      {...(name ? { name } : {})}
      {...(label ? { "aria-label": label } : {})}
      {...(placeholder ? { placeholder } : {})}
      {...(required ? { required: true } : {})}
      {...(t === "search" ? { inputMode: "search" as const } : {})}
    />
  );
  const glyph =
    typeof icon === "string" &&
    (INPUT_ICONS as readonly string[]).includes(icon)
      ? (icon as (typeof INPUT_ICONS)[number])
      : undefined;
  if (!glyph) return input;
  return (
    <InputGroup.Root>
      <InputGroup.Addon position="start">
        <Icon name={glyph} size="sm" />
      </InputGroup.Addon>
      {input}
    </InputGroup.Root>
  );
}

export interface HeadingContent {
  readonly text?: string;
  readonly level?: number;
  /** start | center | end — Duro Heading's own align. */
  readonly align?: string;
}
export function PrimitiveHeading({
  text = "",
  level = 2,
  align,
}: HeadingContent) {
  const { Heading } = useDesignSystem().components;
  const lvl = (
    typeof level === "number" && level >= 1 && level <= 6 ? level : 2
  ) as Level;
  const a = alignOf(align);
  return (
    <Heading level={lvl} {...(a ? { align: a } : {})}>
      {text}
    </Heading>
  );
}

export interface CardContent {
  readonly title?: string;
  readonly body?: string;
  readonly variant?: string;
}
export function PrimitiveCard({ title, body, variant }: CardContent) {
  const { Card, Text } = useDesignSystem().components;
  return (
    <Card variant={oneOf(CARD_VARIANTS, variant, "outlined")} header={title}>
      {body ? <Text as="p">{body}</Text> : null}
    </Card>
  );
}

const ICON_SIZES = ["sm", "md", "lg"] as const;

/** A stored icon name, or undefined for anything outside the glyph set. */
function glyphOf(v: unknown): (typeof INPUT_ICONS)[number] | undefined {
  return typeof v === "string" && (INPUT_ICONS as readonly string[]).includes(v)
    ? (v as (typeof INPUT_ICONS)[number])
    : undefined;
}

/**
 * The design system's `Icon`, named by the HOST: the contract's Icon takes
 * no label, so this span carries it — `role="img"` + `aria-label` when the
 * glyph means something, `aria-hidden` when it is decoration.
 */
function NamedIcon({
  name,
  size,
  label,
}: {
  readonly name: (typeof INPUT_ICONS)[number];
  readonly size: (typeof ICON_SIZES)[number];
  readonly label?: string | undefined;
}) {
  const { Icon } = useDesignSystem().components;
  // A bare span (createElement, not react-strict-dom's html.span): this also
  // renders inside the sandbox, whose runtime has no StyleX pass.
  return createElement(
    "span",
    label ? { role: "img", "aria-label": label } : { "aria-hidden": true },
    <Icon name={name} size={size} />,
  );
}

export interface IconContent {
  readonly name?: string;
  readonly size?: string;
  readonly label?: string;
  readonly align?: string;
}
export function PrimitiveIcon({ name, size, label, align }: IconContent) {
  const { Inline } = useDesignSystem().components;
  const glyph = glyphOf(name) ?? "search";
  const icon = (
    <NamedIcon
      name={glyph}
      size={oneOf(ICON_SIZES, size, "md")}
      label={label || undefined}
    />
  );
  const justify = alignOf(align);
  return justify ? <Inline justify={justify}>{icon}</Inline> : icon;
}
